import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useCustomerScope } from '../contexts/AccountContext';

export interface CustomerDailyReport {
  id: string;
  trip_id: string;
  customer_id: string;
  location_id: string;
  report_date: string;
  day_number: number;
  hours_on_site: string | null;
  crew_on_site: string | null;
  work_completed: string | null;
  tomorrow_plan: string | null;
  submitted_at: string | null;
  delivered_to_customer_at: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Customer-portal-safe read of daily reports.
 *
 * Backed by daily_reports_customer_view which (a) excludes
 * internal_notes / punch_list / lead_captured and (b) hides reports
 * not yet delivered to the customer. Scoped to the account selection as
 * well as the location so nothing from an unselected account renders.
 */
export function useCustomerDailyReports(locationId?: string) {
  const scope = useCustomerScope();

  return useQuery({
    queryKey: ['customer-daily-reports', scope.key, locationId ?? ''],
    enabled: !!locationId && scope.ready && scope.ids.length > 0,
    queryFn: async (): Promise<CustomerDailyReport[]> => {
      const { data, error } = await supabase
        .from('daily_reports_customer_view')
        .select(
          'id, trip_id, customer_id, location_id, report_date, day_number, hours_on_site, crew_on_site, work_completed, tomorrow_plan, submitted_at, delivered_to_customer_at, created_at, updated_at',
        )
        .eq('location_id', locationId!)
        .in('customer_id', scope.ids)
        .order('report_date', { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as CustomerDailyReport[];
    },
    staleTime: 60 * 1000,
  });
}
